import { type IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSquareParking, faCircleExclamation, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { useListDevices } from 'src/features/parkingStatus/api/useListDevices';
import { DeviceCurrentStatusEnum } from 'src/features/parkingStatus/types';
import { ICON_MAP } from 'src/features/parkingStatus/utils';
import { parkingStatusMessages } from 'src/features/parkingStatus/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import {
    GeneralBlock,
    GeneralBlockFooter,
    GeneralBlockHead,
    GeneralBlockHeader,
    GeneralBlockSeparator,
    GeneralBlockSubtitle,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import OverflowTooltip from 'src/registry/components/blocks/OverflownTooltip';
import {
    SectionBlock,
    SectionBlockHeader,
    SectionBlockHead,
    SectionBlockTitle,
    SectionBlockBody,
    SectionBlockSeparator,
    SectionBlockSubtitle,
} from 'src/registry/components/blocks/SectionBlock';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { Badge } from 'src/registry/ui/Badge';
import { Skeleton } from 'src/registry/ui/Skeleton';

function Devices() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { data, isLoading, isError } = useListDevices({ parkingId, limit: 100 });

    return (
        <SectionBlock>
            <SectionBlockHeader>
                <SectionBlockHead>
                    <SectionBlockTitle>{formatMessage(parkingStatusMessages.devices)}</SectionBlockTitle>
                    <SectionBlockSubtitle>{formatMessage(parkingStatusMessages.devicesSubtitle)}</SectionBlockSubtitle>
                </SectionBlockHead>
            </SectionBlockHeader>
            <SectionBlockSeparator />
            <SectionBlockBody className="grid md:grid-cols-2 xl:grid-cols-3">
                {isError && (
                    <GeneralMessageBlock
                        className="py-12 md:col-span-2 xl:col-span-3"
                        variant="error"
                        icon={faCircleExclamation}
                    />
                )}
                {isLoading && Array.from(Array(10).keys()).map((index) => <Skeleton key={index} className="h-32" />)}
                {data &&
                    data.results &&
                    data.results.map((device) => {
                        // @ts-expect-error Weird TS error due to enums
                        const status = DeviceCurrentStatusEnum[device.current_status] as DeviceCurrentStatusEnum;

                        // @ts-expect-error Weird TS error due to enums
                        const icon: IconProp = (ICON_MAP[device.type] as IconProp) ?? faSquareParking;

                        return (
                            <GeneralBlock
                                variant={status}
                                data-state={status}
                                key={device.id}
                                className="flex h-full flex-col"
                            >
                                <GeneralBlockHeader>
                                    <SquareIcon icon={icon} variant={status} className="shrink-0" />
                                    <GeneralBlockHead>
                                        <GeneralBlockTitle>{device.name}</GeneralBlockTitle>
                                        <GeneralBlockSubtitle>{device.code}</GeneralBlockSubtitle>
                                    </GeneralBlockHead>
                                </GeneralBlockHeader>
                                <div className="flex flex-1 flex-col justify-end">
                                    <GeneralBlockSeparator />

                                    <GeneralBlockFooter className="bg- flex h-9 items-end justify-end">
                                        {status === DeviceCurrentStatusEnum.FAULT && (
                                            <Badge theme="red" className="flex max-w-full bg-red-100">
                                                <FontAwesomeIcon
                                                    icon={faTriangleExclamation}
                                                    className="mr-1 shrink-0 text-red-600"
                                                />

                                                <OverflowTooltip text={device.current_status_texts.join(', ')} />
                                            </Badge>
                                        )}

                                        {status === DeviceCurrentStatusEnum.WARNING && (
                                            <Badge theme="orange" className="flex max-w-full items-center">
                                                <FontAwesomeIcon
                                                    icon={faCircleExclamation}
                                                    className="mr-1 shrink-0 text-orange-600"
                                                />

                                                <OverflowTooltip text={device.current_status_texts.join(', ')} />
                                            </Badge>
                                        )}

                                        {status === DeviceCurrentStatusEnum.RUNNING && (
                                            <Badge>
                                                <div className="size-1.5 rounded-full bg-emerald-600"></div>
                                                {formatMessage(parkingStatusMessages.devicesNoIssues)}
                                            </Badge>
                                        )}

                                        {status === DeviceCurrentStatusEnum.STOPPED && (
                                            <Badge>
                                                <div className="size-1.5 rounded-full bg-red-600"></div>
                                                {formatMessage(parkingStatusMessages.devicesTurnedOff)}
                                            </Badge>
                                        )}
                                    </GeneralBlockFooter>
                                </div>
                            </GeneralBlock>
                        );
                    })}
            </SectionBlockBody>
        </SectionBlock>
    );
}

export { Devices };
